// nameAusSuche.ts — ein getippter Suchbegriff als Personenname.
//
// Wer einen Kontakt sucht und nicht findet, soll ihn aus dem Suchbegriff heraus
// anlegen können (globale Suche, Kontaktliste, „neu eintippen" im Kontaktfeld).
// Dafür muss der Begriff in Vor- und Nachname zerfallen — nach denselben Regeln
// an allen Stellen:
//
//   „Nachname, Vorname"  → Komma trennt, der Teil davor ist der Nachname
//   „Vorname Nachname"   → erstes Wort ist der Vorname, der Rest der Nachname
//   „Nachname"           → ein einzelnes Wort ist immer der Nachname
//
// Firmen brauchen das nicht: dort ist der ganze Text der Firmenname.

export type NameAusSuche = { vorname: string; nachname: string };

export const nameAusSuche = (text: string): NameAusSuche => {
  const raw = (text ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return { vorname: "", nachname: "" };
  if (raw.includes(",")) {
    const parts = raw.split(",").map((p) => p.trim());
    return {
      nachname: parts[0] || "",
      vorname: parts.slice(1).filter(Boolean).join(" "),
    };
  }
  if (raw.includes(" ")) {
    const parts = raw.split(" ");
    return { vorname: parts[0] || "", nachname: parts.slice(1).join(" ") };
  }
  return { vorname: "", nachname: raw };
};

/**
 * D4 (Plan 2026-09-30): Kind oder Partner im Haushalt tragen meist den
 * Nachnamen des Hauptkontakts — er wird vorbelegt und bleibt überschreibbar.
 * Mit Vorschlag gilt ein einzelnes getipptes Wort als VORNAME („Lia" im
 * Haushalt Muster → Lia Muster), ein leerer Nachname wird aufgefüllt; ein
 * ausdrücklich getippter Nachname („Lia Berg", „Berg, Lia") gewinnt.
 */
export const nameMitNachnameVorschlag = (text: string, vorschlag?: string | null): NameAusSuche => {
  const v = (vorschlag ?? "").trim();
  const n = nameAusSuche(text);
  if (!v) return n;
  const raw = (text ?? "").replace(/\s+/g, " ").trim();
  if (raw && !raw.includes(",") && !raw.includes(" ")) {
    return raw.toLowerCase() === v.toLowerCase() ? { vorname: "", nachname: v } : { vorname: raw, nachname: v };
  }
  return { vorname: n.vorname, nachname: n.nachname || v };
};

// ---------------------------------------------------------------------------
// Anlage aus der Suche: „Kontakt nicht gefunden – neu anlegen?"
//
// Der Suchbegriff reist als Query-Parameter in die Anlage-Seite und wird dort
// als Vorbelegung gelesen (NewCompanyContactPage / NewPrivateContactPage).
// Firma: `?name=`, Privat: `?vorname=&nachname=`.

export type AnlageAusSucheTyp = "firma" | "privat";

/** Ziel-Route der Anlage mit dem Suchbegriff als Vorbelegung. */
export const anlagePfadAusSuche = (typ: AnlageAusSucheTyp, text: string): string => {
  if (typ === "firma") {
    const name = (text ?? "").replace(/\s+/g, " ").trim();
    return `/contacts/new/company?${new URLSearchParams({ name }).toString()}`;
  }
  const { vorname, nachname } = nameAusSuche(text);
  return `/contacts/new/private?${new URLSearchParams({ vorname, nachname }).toString()}`;
};

type ParamLeser = { get(name: string): string | null };

const param = (p: ParamLeser, name: string): string => (p.get(name) ?? "").trim();

/** Vorbelegung der Firmenanlage aus `?name=` — Feldname `name`. */
export const firmaVorbelegungAusParams = (p: ParamLeser): Record<string, string> => {
  const name = param(p, "name");
  return name ? { name } : {};
};

/** Vorbelegung der Privatanlage aus `?vorname=&nachname=` — Felder `firstname`/`lastname`. */
export const privatVorbelegungAusParams = (p: ParamLeser): Record<string, string> => {
  const out: Record<string, string> = {};
  const vorname = param(p, "vorname");
  const nachname = param(p, "nachname");
  if (vorname) out.firstname = vorname;
  if (nachname) out.lastname = nachname;
  return out;
};
